import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { PhoneIcon, MailIcon, UserIcon } from 'lucide-react';
interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}
interface ContactFormProps {
  theme: 'dark' | 'light';
}
export function ContactForm({
  theme
}: ContactFormProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, {
    once: true,
    margin: '-80px'
  });
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };
  const inputClasses = 'w-full bg-transparent border-0 border-b border-[var(--color-border)] text-villa-text font-inter font-light text-sm py-4 px-0 placeholder:text-villa-text/25 focus:outline-none focus:border-villa-gold transition-colors duration-300';
  return <section id="contact" ref={sectionRef} className="w-full bg-villa-dark py-24 md:py-32 px-6 md:px-10 transition-colors duration-500" aria-label="Kontakt a domluvení schůzky" data-id="element-11">
      <div className="max-w-6xl mx-auto" data-id="element-12">
        <motion.p initial={{
        opacity: 0,
        y: 16
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        ease: 'easeOut'
      }} className="text-villa-gold text-[12px] tracking-[0.3em] uppercase font-inter font-light mb-6" data-id="element-13">
          Kontakt
        </motion.p>

        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.7,
        delay: 0.1,
        ease: 'easeOut'
      }} className="font-playfair text-villa-text text-3xl md:text-4xl lg:text-5xl font-medium mb-16 leading-tight" data-id="element-14">
          Zajistěte si své nové bydlení
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 lg:gap-20" data-id="element-15">
          {/* Form */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.2,
          ease: 'easeOut'
        }} className="lg:col-span-2" data-id="element-16">
            {submitted ? <div className="py-16 text-center" data-id="element-17">
                <p className="font-playfair text-villa-text text-2xl mb-4" data-id="element-18">
                  Děkujeme za váš zájem
                </p>
                <p className="font-inter text-villa-text/50 text-sm font-light" data-id="element-19">
                  Náš tým se vám brzy ozve s dalšími informacemi o projektu
                  Mezírka.
                </p>
              </div> : <form onSubmit={handleSubmit} className="space-y-2" data-id="element-20">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2" data-id="element-21">
                  <div data-id="element-22">
                    <label htmlFor="name" className="block text-villa-text/30 text-[11px] tracking-[0.2em] uppercase font-inter font-light mb-0 pt-4" data-id="element-23">
                      Jméno a příjmení
                    </label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Vaše celé jméno" required className={inputClasses} data-id="element-24" />
                  </div>

                  <div data-id="element-25">
                    <label htmlFor="email" className="block text-villa-text/30 text-[11px] tracking-[0.2em] uppercase font-inter font-light mb-0 pt-4" data-id="element-26">
                      E-mailová adresa
                    </label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="vas@email.cz" required className={inputClasses} data-id="element-27" />
                  </div>

                  <div className="md:col-span-2" data-id="element-28">
                    <label htmlFor="phone" className="block text-villa-text/30 text-[11px] tracking-[0.2em] uppercase font-inter font-light mb-0 pt-4" data-id="element-29">
                      Telefonní číslo
                    </label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+420 000 000 000" className={inputClasses} data-id="element-30" />
                  </div>
                </div>

                <div data-id="element-31">
                  <label htmlFor="message" className="block text-villa-text/30 text-[11px] tracking-[0.2em] uppercase font-inter font-light mb-0 pt-4" data-id="element-32">
                    Zpráva / Dotaz
                  </label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} placeholder="O jakou dispozici máte zájem?" rows={4} className={`${inputClasses} resize-none`} data-id="element-33" />
                </div>

                <div className="pt-8" data-id="element-34">
                  <button type="submit" className="bg-villa-gold text-villa-dark px-12 py-4 text-[13px] tracking-[0.2em] uppercase font-inter font-medium transition-all duration-500 hover:bg-villa-gold/85" data-id="element-35">
                    Odeslat poptávku
                  </button>
                </div>
              </form>}
          </motion.div>

          {/* Agent Info */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.7,
          delay: 0.35,
          ease: 'easeOut'
        }} className="lg:col-span-1" data-id="element-36">
            <div className="border-l border-[var(--color-border)] pl-10" data-id="element-37">
              <p className="text-villa-text/30 text-[11px] tracking-[0.2em] uppercase font-inter font-light mb-8" data-id="element-38">
                Prodejce
              </p>

              <div className="w-20 h-20 rounded-full bg-villa-surface flex items-center justify-center mb-6" data-id="element-39">
                <UserIcon className="w-8 h-8 text-villa-text/20" strokeWidth={1} aria-hidden="true" data-id="element-40" />
              </div>

              <p className="font-playfair text-villa-text text-xl font-medium mb-1" data-id="element-41">
                Jaroslav Kováč
              </p>
              <p className="font-inter text-villa-gold text-[12px] tracking-[0.15em] uppercase font-light mb-8" data-id="element-42">
                FLATS DEVELOPMENT BRNO s.r.o.
              </p>

              <div className="space-y-5" data-id="element-43">
                <a href="tel:+420608150100" className="flex items-center gap-4 text-villa-text/50 transition-colors duration-300 hover:text-villa-gold" data-id="element-44">
                  <PhoneIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.2} aria-hidden="true" data-id="element-45" />
                  <span className="font-inter text-sm font-light" data-id="element-46">
                    +420 608 150 100
                  </span>
                </a>

                <a href="mailto:kovac@homehunters.cz" className="flex items-center gap-4 text-villa-text/50 transition-colors duration-300 hover:text-villa-gold" data-id="element-47">
                  <MailIcon className="w-4 h-4 flex-shrink-0" strokeWidth={1.2} aria-hidden="true" data-id="element-48" />
                  <span className="font-inter text-sm font-light" data-id="element-49">
                    kovac@homehunters.cz
                  </span>
                </a>
              </div>

              <div className="mt-10 pt-8 border-t border-[var(--color-border)]" data-id="element-50">
                <p className="font-inter text-villa-text/30 text-xs leading-relaxed font-light" data-id="element-51">
                  Stavitel: Togner & partneři s.r.o. <br data-id="element-52" />
                  Developer si vyhrazuje právo změnit specifikované výrobky za
                  technicky srovnatelné alternativy bez předchozího upozornění.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}